import { inject } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store, createSelector } from '@ngrx/store';
import {
  EMPTY,
  catchError,
  concatMap,
  distinctUntilChanged,
  exhaustMap,
  filter,
  from,
  groupBy,
  map,
  merge,
  mergeMap,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

import {
  AegisDocumentPreviewGQL,
  BaseArtifactFragment,
  CreateAegisDocumentGQL,
  GetConfigValuesGQL,
  SendDocumentGQL,
} from '../../../generated/graphql.generated';
import { zonesFeature } from '../../core/zones/zones.reducer';
import { JobDocumentsActions } from '../../job/job-files/job-files.actions';
import { JobHeaderActions } from '../../job/job-header/job-header.actions';
import { JobToolActions } from '../../job/job-tool.actions';
import { selectJob, selectJobId } from '../../job/job-tool.reducer';
import { WorkOrdersActions } from '../../job/job-workorders/job-workorders.actions';
import { NewOpportunityActions } from '../../new-opportunity/new-opportunity.actions';
import { DetailsHelperService } from '../../services/details-helper.service';
import { DocumentHelperService } from '../../services/document-helper.service';
import { FreyaHelperService } from '../../services/freya-helper.service';
import { selectJobSaveChanges } from '../unsaved-changes-banner/unsaved-changes-banner.selectors';

import { CreateDocumentDialogActions } from './create-document-dialog.actions';
import { createDocumentDialogFeature } from './create-document-dialog.feature';
import { selectOrderedDocuments } from './create-document-dialog.selectors';
import { AegisDocumentPreview, CreateDocumentDraft, CreateDocumentSubmission } from './create-document-dialog.state';
import { declaredValues, estimateBlockedReason, isBillableEvent } from './create-document-dialog.util';
import {
  DEFAULT_ESTIMATE_KEY,
  DOCUMENT_ORDER_KEY,
  DocumentTypesActions,
  documentTypesFeature,
  orderDocumentSlots,
  parseDefaultEstimate,
  parseDocumentOrder,
  parseDocumentSlots,
} from './document-types';

export const loadDocumentTypes = createEffect((
  store = inject(Store),
  getConfigValuesGQL = inject(GetConfigValuesGQL),
) => {
  return store.select(createSelector(zonesFeature.selectCurrentAppZone, (zone) => zone?.id)).pipe(
    distinctUntilChanged(),
    switchMap((zoneId) => !zoneId ? EMPTY : getConfigValuesGQL.fetch({
      keys: [ 'standard-documents.*', DEFAULT_ESTIMATE_KEY, DOCUMENT_ORDER_KEY ],
    }).pipe(
      map(({ data: { getConfigValues: configs } }) => DocumentTypesActions.loadSuccess({
        zoneId,
        slots: orderDocumentSlots(parseDocumentSlots(configs), parseDocumentOrder(configs)),
        defaultEstimate: parseDefaultEstimate(configs),
      })),
      catchError((error) => of(DocumentTypesActions.loadError({ zoneId, error }))),
    )),
  );
}, { functional: true, dispatch: true });

export const openFromJobPage = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
  return actions$.pipe(
    ofType(JobDocumentsActions.createDocumentButtonClicked, JobHeaderActions.sendDocumentMenuItemClicked),
    concatLatestFrom(() => store.select(selectJobId)),
    filter(([ _action, jobId ]) => Boolean(jobId)),
    map(([ _action, jobId ]) => CreateDocumentDialogActions.dialogOpened({ jobId })),
  );
}, { functional: true, dispatch: true });

export const openEstimateFromJobPage = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
  const addEstimateClickedNoChanges$ = actions$.pipe(
    ofType(WorkOrdersActions.addEstimateButtonClicked),
    // Pending Crew Pay boxes on a shared job are left for the banner: they never reach the
    // full save, so waiting on it would never fire, and the document does not show crew pay.
    concatLatestFrom(() => store.select(selectJobSaveChanges)),
    filter(([ _action, changes ]) => changes.length === 0),
  );

  const addEstimateClickedChangesSaved$ = actions$.pipe(
    ofType(JobToolActions.saveChangesSuccess),
    filter((action) => action.promptedBy.type === WorkOrdersActions.addEstimateButtonClicked.type),
  );

  const navigatedFromNewOpportunity$ = actions$.pipe(
    ofType(NewOpportunityActions.navigateToJobPageSuccess),
    filter(({ generateEstimatePreview }) => generateEstimatePreview),
  );

  return merge(addEstimateClickedNoChanges$, addEstimateClickedChangesSaved$, navigatedFromNewOpportunity$).pipe(
    concatLatestFrom(() => store.select(selectJob)),
    filter(([ _action, job ]) => Boolean(job?.id) && !estimateBlockedReason(Boolean(job.events?.some(isBillableEvent)))),
    switchMap(([ _action, job ]) => store.select(createSelector(
      zonesFeature.selectCurrentAppZone,
      documentTypesFeature.selectZoneId,
      documentTypesFeature.selectLoaded,
      documentTypesFeature.selectDefaultEstimate,
      (zone, zoneId, loaded, defaultEstimate) => (loaded && zone?.id === zoneId ? defaultEstimate : undefined),
    )).pipe(
      filter(Boolean),
      take(1),
      map((docType) => CreateDocumentDialogActions.dialogOpened({ jobId: job.id, docType })),
    )),
  );
}, { functional: true, dispatch: true });

export const loadPreview = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  previewGQL = inject(AegisDocumentPreviewGQL),
) => {
  const dialogOpened$ = actions$.pipe(ofType(
    CreateDocumentDialogActions.dialogOpened,
    CreateDocumentDialogActions.jobActionsCreateDocumentClicked,
    CreateDocumentDialogActions.sendReceiptButtonClicked,
  ));
  return actions$.pipe(
    ofType(
      CreateDocumentDialogActions.dialogOpened,
      CreateDocumentDialogActions.jobActionsCreateDocumentClicked,
      CreateDocumentDialogActions.sendReceiptButtonClicked,
      CreateDocumentDialogActions.documentCheckboxClicked,
      CreateDocumentDialogActions.documentRowClicked,
      CreateDocumentDialogActions.retryPreviewButtonClicked,
    ),
    concatLatestFrom(() => store.select(createDocumentDialogFeature.selectCreateDocumentDialogState)),
    filter(([ _action, state ]) => state.open && Boolean(state.jobId)),
    mergeMap(([ _action, { session, jobId, documents } ]) => from(documents
      .filter((doc) => doc.previewLoading)
      .map(({ docType }) => ({ session, jobId, docType })))),
    groupBy(({ session, docType }) => `${session}:${docType}`, { duration: () => dialogOpened$ }),
    mergeMap((requests$) => requests$.pipe(
      exhaustMap(({ session, jobId, docType }) => previewGQL.fetch(
        { jobId, docType },
        { fetchPolicy: 'network-only' },
      ).pipe(
        map((res) => CreateDocumentDialogActions.previewLoaded({
          session,
          preview: res.data.aegisDocumentPreview as AegisDocumentPreview,
        })),
        catchError((error) => of(CreateDocumentDialogActions.previewFailed({ session, docType, error }))),
      )),
    )),
  );
}, { functional: true, dispatch: true });

const createDocument = (
  createGQL: CreateAegisDocumentGQL,
  session: number,
  jobId: string,
  submission: CreateDocumentSubmission,
  { docType, preview, values, message }: CreateDocumentDraft,
) => createGQL.mutate({
  jobId,
  docType,
  options: declaredValues(preview.options, values),
  send: submission === 'send',
  message: submission === 'send' && message.trim() ? message.trim() : undefined,
}).pipe(
  map((res) => {
    const { artifact, sent, sendError } = res.data.createAegisDocument;
    return CreateDocumentDialogActions.documentCreated({
      session,
      jobId,
      docType,
      artifact,
      sent,
      sendError: sendError ?? undefined,
    });
  }),
  catchError((error) => of(CreateDocumentDialogActions.createDocumentFailed({ session, docType, error }))),
);

export const createDocuments = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  createGQL = inject(CreateAegisDocumentGQL),
) => {
  return actions$.pipe(
    ofType(
      CreateDocumentDialogActions.submitButtonClicked,
      CreateDocumentDialogActions.createWithoutSendingMenuItemClicked,
    ),
    concatLatestFrom(() => [
      store.select(createDocumentDialogFeature.selectCreateDocumentDialogState),
      store.select(selectOrderedDocuments),
    ]),
    filter(([ _action, state ]) => Boolean(state.submitting)),
    exhaustMap(([ _action, { session, jobId, submitting }, documents ]) => from(documents).pipe(
      concatMap((doc) => createDocument(createGQL, session, jobId, submitting, doc)),
    )),
  );
}, { functional: true, dispatch: true });

export const recreateDocument = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  createGQL = inject(CreateAegisDocumentGQL),
) => {
  return actions$.pipe(
    ofType(CreateDocumentDialogActions.tryCreatingAgainButtonClicked),
    concatLatestFrom(() => store.select(createDocumentDialogFeature.selectCreateDocumentDialogState)),
    map(([ { docType }, { session, jobId, submitted, documents } ]) => ({
      session,
      jobId,
      submitted,
      document: documents.find((doc) => doc.docType === docType),
    })),
    filter(({ submitted, document }) => Boolean(submitted && document?.creating)),
    groupBy(({ session, document }) => `${session}:${document.docType}`),
    mergeMap((requests$) => requests$.pipe(
      exhaustMap(({ session, jobId, submitted, document }) => createDocument(createGQL, session, jobId, submitted, document)),
    )),
  );
}, { functional: true, dispatch: true });

export const resendDocument = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  sendGQL = inject(SendDocumentGQL),
) => {
  return actions$.pipe(
    ofType(CreateDocumentDialogActions.trySendingAgainButtonClicked),
    concatLatestFrom(() => store.select(createDocumentDialogFeature.selectCreateDocumentDialogState)),
    map(([ { docType }, { session, jobId, documents } ]) => ({
      session,
      jobId,
      document: documents.find((doc) => doc.docType === docType),
    })),
    filter(({ document }) => Boolean(document?.resending && document.result?.artifact)),
    groupBy(({ session, document }) => `${session}:${document.docType}`),
    mergeMap((requests$) => requests$.pipe(
      exhaustMap(({ session, jobId, document: { docType, result, message } }) => sendGQL.mutate({
        artifactId: result.artifact.id,
        recipientRole: 'customer',
        message: message.trim() || undefined,
      }).pipe(
        map(() => CreateDocumentDialogActions.resendSucceeded({ session, jobId, docType, artifactId: result.artifact.id })),
        catchError((error) => of(CreateDocumentDialogActions.resendFailed({ session, docType, error }))),
      )),
    )),
  );
}, { functional: true, dispatch: true });

export const pushArtifactUpdate = createEffect((
  actions$ = inject(Actions),
  detailsHelper = inject(DetailsHelperService),
) => {
  return merge(
    actions$.pipe(
      ofType(CreateDocumentDialogActions.documentCreated),
      map(({ artifact }) => ({ id: artifact.id, action: 'create' as const })),
    ),
    actions$.pipe(
      ofType(CreateDocumentDialogActions.resendSucceeded),
      map(({ artifactId }) => ({ id: artifactId, action: 'update' as const })),
    ),
  ).pipe(
    tap(({ id, action }) => detailsHelper.pushUpdate({ id, type: 'Artifacts', action })),
  );
}, { functional: true, dispatch: false });

export const downloadDocument = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  freyaHelper = inject(FreyaHelperService),
) => {
  return actions$.pipe(
    ofType(CreateDocumentDialogActions.downloadButtonClicked),
    concatLatestFrom(() => store.select(createDocumentDialogFeature.selectDocuments)),
    map(([ { docType }, documents ]) => documents.find((doc) => doc.docType === docType)?.result?.artifact),
    filter(Boolean),
    tap(({ signedUrl, url, name }: BaseArtifactFragment) => freyaHelper.downloadFile(signedUrl || url, name)),
  );
}, { functional: true, dispatch: false });

export const openCreateInvoiceDialog = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  documentHelper = inject(DocumentHelperService),
) => {
  return actions$.pipe(
    ofType(CreateDocumentDialogActions.createInvoiceLinkClicked),
    concatLatestFrom(() => store.select(createDocumentDialogFeature.selectJobId)),
    filter(([ _action, jobId ]) => Boolean(jobId)),
    tap(([ _action, jobId ]) => documentHelper.openCreateInvoiceDialog(jobId)),
  );
}, { functional: true, dispatch: false });
